import { memo, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { Avatar } from '@/components/atoms';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import type { Notification } from '@/lib/notification-types';
import {
  getGroupedDisplayText,
  getNotificationRoute,
} from '@/lib/notification-types';
import { resolveNotificationTarget } from '@/lib/notification-target';
import { useNotificationNavigation } from '@/hooks/useNotificationNavigation';
import { logger } from '@/lib/logger';

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: string) => void;
  onGroupRead: (params: { notificationType: string; entityId: string; referencePostId?: string }) => void;
  onClose?: () => void;
}

/**
 * NotificationItem - Single notification row in dropdown
 * Displays actor avatar, message, timestamp, and unread indicator
 * Opens modals in-place for post/photo notifications (no navigation)
 * Supports grouped notifications (Phase 13)
 */
export const NotificationItem = memo<NotificationItemProps>(
  function NotificationItem({ notification, onRead, onGroupRead, onClose }) {
    const { handleNavigationTarget } = useNotificationNavigation();
    const [isNavigating, setIsNavigating] = useState(false);
    const isUnread = !notification.read_at;

    const displayText = getGroupedDisplayText(notification);
    const routeResult = getNotificationRoute(notification);

    const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
      addSuffix: true,
    });

    const handleClick = useCallback(async () => {
      if (isNavigating) return;

      // Mark as read on click — use group read for grouped notifications
      if (isUnread) {
        if (notification.grouped_count > 1 && notification.entity_id) {
          const isCommentGroup = ['comment', 'reply'].includes(notification.notification_type);
          onGroupRead({
            notificationType: notification.notification_type,
            entityId: notification.entity_id,
            referencePostId: isCommentGroup ? notification.reference_post_id ?? undefined : undefined,
          });
        } else {
          onRead(notification.id);
        }
      }

      setIsNavigating(true);
      try {
        const target = resolveNotificationTarget(routeResult);
        handleNavigationTarget(target);
      } catch (err) {
        logger.error('Failed to resolve notification target:', err);
        toast.error('Could not open notification. Please try again.');
      } finally {
        setIsNavigating(false);
      }

      onClose?.();
    }, [isUnread, isNavigating, notification, onRead, onGroupRead, routeResult, onClose, handleNavigationTarget]);

    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={isNavigating}
        className={cn(
          'flex w-full items-start gap-3 p-3 text-left transition-colors',
          'hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-none',
          isUnread && 'bg-accent/30',
          isNavigating && 'opacity-50 cursor-wait'
        )}
      >
        {/* Avatar */}
        <Avatar
          src={notification.actor.avatar_url}
          firstName={notification.actor.first_name}
          lastName={notification.actor.last_name}
          username={notification.actor.username}
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground">
            {displayText}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{timeAgo}</p>
        </div>

        {/* Unread indicator */}
        {isUnread && (
          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
        )}
      </button>
    );
  }
);
